// useVideoMeta — #960 client-side fallback for video thumbnail + duration.
// The live api/media/ shape (mediaAdapter.js) carries no `thumbnail` and no
// `duration` for video files, so tiles fell back to a generic glyph and a
// lying "0:00" chip for every clip. Mirrors the existing PosterScrubber
// (components/PortraitMedia) pattern: a hidden <video>, read `.duration` from
// `loadedmetadata`, seek to a representative frame and paint it to a <canvas>
// for a real poster image. Results are cached per URL so the grid (and any
// list/small-view re-render) never re-decodes the same clip twice.
import { useEffect, useState } from "react";

const cache = new Map(); // url -> { duration: number|null, poster: string|null }

function loadVideoMeta(url) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;
    const canvas = document.createElement("canvas");
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      resolve(result);
    };

    video.onerror = () => finish({ duration: null, poster: null });

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      video.onseeked = () => {
        try {
          const vw = video.videoWidth || 160;
          const vh = video.videoHeight || 90;
          canvas.width = 160;
          canvas.height = Math.max(1, Math.round((160 * vh) / vw));
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          finish({ duration, poster: canvas.toDataURL("image/jpeg", 0.7) });
        } catch (e) {
          finish({ duration, poster: null }); // tainted canvas (no CORS) — keep the duration
        }
      };
      try {
        // A representative (non-black) frame: 10% in, capped at 1s.
        video.currentTime = duration ? Math.min(1, duration * 0.1) : 0;
      } catch (e) {
        finish({ duration, poster: null });
      }
    };

    video.src = url;
  });
}

const EMPTY = { duration: null, poster: null };

// `enabled` gates the decode (only video items missing BOTH a BE thumbnail and
// a BE duration need it) so idle tiles never spin up a hidden <video>.
export function useVideoMeta(url, enabled) {
  const [meta, setMeta] = useState(() => (url && cache.has(url) ? cache.get(url) : EMPTY));

  useEffect(() => {
    if (!enabled || !url) return undefined;
    if (cache.has(url)) {
      setMeta(cache.get(url));
      return undefined;
    }
    let cancelled = false;
    loadVideoMeta(url).then((result) => {
      cache.set(url, result);
      if (!cancelled) setMeta(result);
    });
    return () => {
      cancelled = true;
    };
  }, [url, enabled]);

  return meta;
}
